import Store from '@ember-data/store';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  BulkActionCustomChangeParentCampaign,
  BulkActionResponseCampaignId,
  BulkActionType,
} from 'donor-elf-web/models/bulk-action';
import AddSomethingModal, {
  type OpenArgs,
} from 'donor-elf-web/services/add-something-modal';
import CampaignService from 'donor-elf-web/services/campaign-service';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';

interface ComponentArgs {
  openArgs?: OpenArgs;
}

export default class AddSomethingModalChangeParentCampaignIndexComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
  }

  @service declare addSomethingModal: AddSomethingModal;
  @service declare store: Store;
  @service declare campaignService: CampaignService;

  bulkActionType: BulkActionType = 'change_parent_campaign';
  @tracked bulkActionCustom?: BulkActionCustomChangeParentCampaign;

  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked saving = false;
  @tracked saveError?: any;

  get campaign() {
    return this.args.openArgs!.changeParentCampaignOptions!.campaign;
  }

  get parentCampaignSelectOptions() {
    return [
      { val: undefined, label: 'None' },
      ...this.campaignService.sortedCampaignSelectOptions,
    ];
  }

  get changesetParentCampaignLabel() {
    return this.campaignService.campaignsById[
      this.changeset.get('parentCampaignId')
    ]?.label;
  }

  createChangeset() {
    let { campaign, parentCampaignValidation } = this;
    this.changeset = new Changeset(
      campaign,
      lookupValidator(parentCampaignValidation),
      parentCampaignValidation,
    );
  }

  get parentCampaignValidation() {
    return {
      parentCampaignId: this.validateParentCampaignId,
    };
  }

  validateParentCampaignId = (
    _key: string,
    newValue: string,
    oldValue: string,
    _changes: any,
    _content: any,
  ): ValidationResult => {
    if (newValue === oldValue) {
      return "Parent Campaign didn't change";
    }
    if (isPresent(newValue)) {
      if (this.campaign.id === newValue) {
        return "Can't set parent campaign itself";
      }
      if (this.campaign.idAndDescendantIds.includes(newValue)) {
        return "Can't change parent campaign to one of its descendant campaigns";
      }
      let newParentCampaign = this.campaignService.campaignsById[newValue];
      if (
        newParentCampaign &&
        newParentCampaign.restricted !== this.campaign.restricted
      ) {
        if (newParentCampaign.restricted) {
          return "Can't change to this parent campaign because it's restricted";
        } else {
          return "Can't change to this parent campaign because the campaign is restricted";
        }
      }
    }
    return true;
  };

  save = async () => {
    if (this.saving) {
      return;
    }
    this.showValidationErrors = false;
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      this.bulkActionCustom = {
        campaign_id: this.campaign.id,
        new_parent_campaign_id: this.changeset.get('parentCampaignId') || '',
      };
      this.saving = true;
    } else {
      this.showValidationErrors = true;
    }
  };

  onCompleted = async (_response: BulkActionResponseCampaignId) => {
    let { campaign } = this;
    await this.campaignService.onParentCampaignChanged({
      campaign,
      previousParentCampaignId: campaign.parentCampaignId,
    });
    this.addSomethingModal.close();
    this.saving = false;
  };

  onError = () => {
    this.saveError = true;
    this.saving = false;
  };

  close = () => {
    this.addSomethingModal.close();
  };
}
